import { create } from 'zustand';
import {
  SelectData,
  FiltersData,
  SavingFiltersData,
  SaveFiltersResponseData,
  Preferences,
  AderenciaBigNumbersData,
  ProductByRangeConfig,
  AderenciaBigNumbersBody,
  DetailsResponse,
  AderenciaDetailsBody,
  HistorySerieDataPoint,
  FileDownloadStatusType,
} from './types';
import { denormalizeUfFilterValues, normalizeUfFilterValues, toUfFilterSelectItems } from './utils';

const createEmptyFilters = (): FiltersData => ({ list: [] as SelectData[], value: [], cache: [] as SelectData[] });

type State = {
  bigNumbersData: any;
  showModalSpeakWithSpecialistAderenciaPrecos: boolean;
  produto: FiltersData;
  rede: FiltersData;
  tipoLoja: FiltersData;
  uf: FiltersData;
  segmento: FiltersData;
  produtoDetailing: FiltersData;
  redeDetailing: FiltersData;
  tipoLojaDetailing: FiltersData;
  ufDetailing: FiltersData;
  savedFilterSelectList: SelectData[];
  savedFilterResponseData: SaveFiltersResponseData[];
  savingFiltersData: SavingFiltersData;
  showModalSavingFilters: boolean;
  aderenciaPreferenceMutateData: Preferences;
  aderenciaBigNumbersData: AderenciaBigNumbersData;
  aderenciaProductByRangeData: ProductByRangeConfig;
  aderenciaBodyRequest: AderenciaBigNumbersBody;
  aderenciaDetailsBodyRequest: AderenciaDetailsBody;
  aderenciaDetailsData: DetailsResponse;
  historyPercentualForaRange: HistorySerieDataPoint[];
  historyQuantidadeDataPoint: HistorySerieDataPoint[];
  detailsDownloadType: 'CSV' | 'XLSX';
  fileDownloadStatusType: FileDownloadStatusType;
  showModalDetailsDownload: boolean;
};

type Actions = {
  setAderenciaPrecosState: (name: string, data: any) => void;
  setAderenciaSavingFiltersName: (name: string) => void;
  setAderenciaSavingFiltersData: (data: any) => void;
  setAderenciaCleanFilters: () => void;
  setAderenciaSelectSavedFilter: (id: number, callback: (body: AderenciaBigNumbersBody) => void, callbackProduct: (query: string) => void) => void;
  setAderenciaBodyRequests: (name: string, value: (string | number)[]) => void;
  setAderenciaDetailsBodyRequests: (name: string, value: (string | number)[]) => void;
  setAderenciaDetailsValuesCleanFilters: () => void;
  reset: () => void;
};

export const aderenciaInitialState: State = {
  bigNumbersData: {} as any,
  showModalSpeakWithSpecialistAderenciaPrecos: false,
  produto: createEmptyFilters(),
  rede: createEmptyFilters(),
  tipoLoja: createEmptyFilters(),
  uf: createEmptyFilters(),
  segmento: createEmptyFilters(),
  produtoDetailing: createEmptyFilters(),
  redeDetailing: createEmptyFilters(),
  tipoLojaDetailing: createEmptyFilters(),
  ufDetailing: createEmptyFilters(),
  savedFilterSelectList: [] as SelectData[],
  savedFilterResponseData: [] as SaveFiltersResponseData[],
  savingFiltersData: {
    nomeFiltroSalvo: '',
    filtros: {
      dataInicial: '',
      produto: [],
      rede: [],
      tipoLoja: [],
      uf: [],
      segmento: [],
    },
  },
  showModalSavingFilters: false,
  aderenciaPreferenceMutateData: {} as Preferences,
  aderenciaBigNumbersData: {} as AderenciaBigNumbersData,
  aderenciaProductByRangeData: {} as ProductByRangeConfig,
  aderenciaBodyRequest: {
    produto: [],
    rede: [],
    tipoLoja: [],
    uf: [],
    segmentosPersonalizados: [],
    dataInicial: '',
    dataFinal: '',
    segmentosPersonalizados: [],
  },
  aderenciaDetailsBodyRequest: {
    produto: [],
    rede: [],
    tipoLoja: [],
    uf: [],
    dataInicial: '',
    dataFinal: '',
    page: 0,
    size: 20,
    campoOrdenacao: 'TOTAL_PRECOS_FORA_DO_RANGE',
    ordemOrdenacao: 'DECRESCENTE',
    segmentosPersonalizados: [],
  },
  aderenciaDetailsData: {} as DetailsResponse,
  historyPercentualForaRange: [],
  historyQuantidadeDataPoint: [],
  detailsDownloadType: 'CSV',
  fileDownloadStatusType: 'willReceiveByEmail',
  showModalDetailsDownload: false,
};

export const useAderenciaPrecosStore = create<State & Actions>(set => ({
  ...aderenciaInitialState,
  setAderenciaPrecosState: (name, data) => {
    set(() => ({
      [name]: data,
    }));
  },
  setAderenciaSavingFiltersName: (name: string) => {
    set(state => ({
      savingFiltersData: {
        ...state.savingFiltersData,
        nomeFiltroSalvo: name,
      },
    }));
  },
  setAderenciaSavingFiltersData: (data: any) => {
    set(() => ({
      savingFiltersData: data,
    }));
  },
  setAderenciaCleanFilters: () => {
    set(state => ({
      produto: { ...state.produto, value: [], cache: [] },
      rede: { ...state.rede, value: [], cache: [] },
      tipoLoja: { ...state.tipoLoja, value: [], cache: [] },
      uf: { ...state.uf, value: [], cache: [] },
      segmento: { ...state.segmento, value: [], cache: [] },
      aderenciaBodyRequest: {
        ...state.aderenciaBodyRequest,
        dataInicial: '',
        dataFinal: '',
        produto: [],
        rede: [],
        tipoLoja: [],
        uf: [],
        segmentosPersonalizados: [],
      },
    }));
  },
  setAderenciaDetailsValuesCleanFilters: () => {
    set(state => ({
      produtoDetailing: { ...state.produtoDetailing, value: [] },
      redeDetailing: { ...state.redeDetailing, value: [] },
      tipoLojaDetailing: { ...state.tipoLojaDetailing, value: [] },
      ufDetailing: { ...state.ufDetailing, value: [] },
    }));
  },
  setAderenciaSelectSavedFilter: (id, callback, callbackProduct) => {
    set(state => {
      const item = state.savedFilterResponseData?.find(item => item.idFiltroSalvo === id);
      const productQuery = item?.produto?.join(' ') || '';

      const bodyRequestData = {
        produto: item?.produto || [],
        rede: item?.rede || [],
        tipoLoja: item?.tipoLoja || [],
        uf: normalizeUfFilterValues(item?.uf || []),
        segmentosPersonalizados: item?.segmento || [],
      };

      const createCache = (items: string[] | undefined) => items?.map(i => ({ label: i, value: i })) || [];

      const requestBody = {
        ...state.aderenciaBodyRequest,
        ...bodyRequestData,
      };

      const requestDetailsBody = {
        ...state.aderenciaDetailsBodyRequest,
        ...bodyRequestData,
      };
      callbackProduct(productQuery);
      callback(requestBody);

      const getFilterState = (filterValues: string[] | undefined) => ({
        value: filterValues || [],
        cache: createCache(filterValues),
        list: createCache(filterValues),
      });

      const getUfFilterState = (filterValues: string[] | undefined) => {
        const displayValues = denormalizeUfFilterValues(filterValues || []);
        const items = toUfFilterSelectItems(filterValues || []);

        return {
          value: displayValues,
          cache: items,
          list: items,
        };
      };

      return {
        rede: getFilterState(item?.rede),
        tipoLoja: getFilterState(item?.tipoLoja),
        uf: getUfFilterState(item?.uf),
        segmento: getFilterState(item?.segmento),
        aderenciaBodyRequest: requestBody,
        aderenciaDetailsBodyRequest: requestDetailsBody,
      };
    });
  },
  setAderenciaBodyRequests: (name: string, value: string[]) => {
    const normalizedValue = name === 'uf' ? normalizeUfFilterValues(value || []) : value || [];

    set(state => ({
      aderenciaBodyRequest: {
        ...state.aderenciaBodyRequest,
        [name]: normalizedValue,
      },
    }));
  },
  setAderenciaDetailsBodyRequests: (name: string, value: string[]) => {
    const normalizedValue = name === 'uf' ? normalizeUfFilterValues(value || []) : value || [];

    set(state => ({
      aderenciaDetailsBodyRequest: {
        ...state.aderenciaDetailsBodyRequest,
        [name]: normalizedValue,
      },
    }));
  },
  reset: () => {
    set(aderenciaInitialState);
  },
}));
